import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { graphRequest } from '../msalConfig';
import UndoToast from '../watch/UndoToast';
import { createPending, UNDO_MS } from '../watch/pendingActions';
import { loadIntentions, addRow, patchRow, removeRow } from './intercessionsApi';
import {
  renderModel, sortRows, nextSortOrder, swapOrder, sortAfter, bandById,
  titleLength, titleTooLong, guideOf, isActive, BANDS, KINDS, TITLE_MAX,
} from './intercessions';
import './IntercessionsCard.css';

// ============================================================
// INTERCESSIONS — the family's evening intercessions, the third CENTCOM
// prayer pill. Pray mode is the rite in order (spec §2.1). Edit mode is
// every row, live-edited, with the Watch 5-second undo behind delete.
//
// Reads/writes Prayer_Intentions through intercessionsApi. Row shape is the
// raw list row; absent Active is active. A delete is optimistic and held
// (pendingActions); a tab close inside the window flushes it with keepalive.
// ============================================================

const ROLE_LABEL = { leader: 'Leader:', all: 'All:' };

export default function Intercessions({ onClose }) {
  const { instance, accounts } = useMsal();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('pray');
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);

  // Add intention / add line drafts.
  const [draft, setDraft] = useState('');
  const [band, setBand] = useState('sick');
  const [lineOpen, setLineOpen] = useState(false);
  const [lineDraft, setLineDraft] = useState('');
  const [lineKind, setLineKind] = useState('Leader');
  const [lineAfter, setLineAfter] = useState('');

  const pendingRef = useRef(null);
  if (!pendingRef.current) pendingRef.current = createPending({ delayMs: UNDO_MS });
  useEffect(() => {
    const flush = () => { pendingRef.current.flushAll({ keepalive: true }); };
    window.addEventListener('pagehide', flush);
    window.addEventListener('beforeunload', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
      window.removeEventListener('beforeunload', flush);
      // Closing the card commits anything still held — closing is not undo.
      pendingRef.current.flushAll();
    };
  }, []);

  const getToken = useCallback(async () => {
    const account = accounts[0];
    if (!account) throw new Error('Not signed in');
    const res = await instance.acquireTokenSilent({ ...graphRequest, account });
    return res.accessToken;
  }, [instance, accounts]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try { const list = await loadIntentions(await getToken()); if (alive) setRows(list); }
      catch (e) { if (alive) setError(e.message); }
    })();
    return () => { alive = false; };
  }, [getToken]);

  const ordered = useMemo(() => sortRows(rows || []), [rows]);
  const prayModel = useMemo(() => renderModel(rows || []), [rows]);

  // ── Writes ──────────────────────────────────────────────────────────────────
  const replaceRow = (id, next) => setRows((list) => list.map((r) => (r.id === id ? next : r)));

  const patch = async (row, patchObj) => {
    const prev = row;
    const optimistic = { ...row };
    if (patchObj.title !== undefined) optimistic.Title = patchObj.title;
    if (patchObj.kind !== undefined) optimistic.Kind = patchObj.kind;
    if (patchObj.sortOrder !== undefined) optimistic.SortOrder = patchObj.sortOrder;
    if (patchObj.active !== undefined) optimistic.Active = patchObj.active;
    if (patchObj.guide !== undefined) { if (String(patchObj.guide).trim()) optimistic.Guide = patchObj.guide.trim(); else delete optimistic.Guide; }
    replaceRow(row.id, optimistic);
    try { await patchRow(await getToken(), row.id, patchObj); setError(null); }
    catch (e) { replaceRow(row.id, prev); setError(e.message); }
  };

  const showUndo = (text, key) => setToast({
    key, text, ttl: UNDO_MS,
    onUndo: () => { pendingRef.current.undo(key); setToast((t) => (t && t.key === key ? null : t)); },
  });
  const clearToast = (key) => setToast((t) => (t && t.key === key ? null : t));

  // One held write per row id: the row leaves the list at once, the DELETE
  // fires when the window closes, undo puts it back with no network call.
  const holdWrite = ({ key, label, apply, revert, run }) => {
    apply();
    pendingRef.current.schedule(key, {
      run: async (opts) => {
        clearToast(key);
        try { await run(opts); setError(null); }
        catch (e) { revert(); setError(`${label}: ${e.message}`); }
      },
      restore: revert,
    });
    showUndo(`${label} · Undo`, key);
  };

  const remove = (row) => {
    const snapshot = rows;
    holdWrite({
      key: row.id,
      label: 'Removed',
      apply: () => setRows((list) => list.filter((r) => r.id !== row.id)),
      revert: () => setRows(snapshot),
      run: async (opts) => removeRow(await getToken(), row.id, { keepalive: opts?.keepalive === true }),
    });
  };

  const move = async (row, dir) => {
    const i = ordered.findIndex((r) => r.id === row.id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ordered.length) return;
    const other = ordered[j];
    const [wa, wb] = swapOrder(row, other);
    const prev = rows;
    setRows((list) => list.map((r) => (r.id === wa.id ? { ...r, SortOrder: wa.SortOrder } : r.id === wb.id ? { ...r, SortOrder: wb.SortOrder } : r)));
    try {
      const token = await getToken();
      await patchRow(token, wa.id, { sortOrder: wa.SortOrder });
      await patchRow(token, wb.id, { sortOrder: wb.SortOrder });
      setError(null);
    } catch (e) { setRows(prev); setError(e.message); }
  };

  const addIntention = async (e) => {
    e?.preventDefault?.();
    const title = draft.trim();
    if (!title || titleTooLong(title) || busy) return;
    const b = bandById(band);
    setBusy(true);
    try {
      const created = await addRow(await getToken(), { title, kind: 'Intention', sortOrder: nextSortOrder(b, rows || []), guide: b.guide });
      setRows((list) => [...list, created]);
      setDraft('');
      setError(null);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const addLine = async (e) => {
    e?.preventDefault?.();
    const title = lineDraft.trim();
    if (!title || titleTooLong(title) || busy) return;
    const after = ordered.find((r) => r.id === lineAfter) || ordered[ordered.length - 1];
    setBusy(true);
    try {
      const created = await addRow(await getToken(), { title, kind: lineKind, sortOrder: after ? sortAfter(after) : 10, guide: '' });
      setRows((list) => [...list, created]);
      setLineDraft('');
      setLineOpen(false);
      setError(null);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="Centcom-prayer-backdrop Intercessions-backdrop" onClick={onClose}>
      <div className="Centcom-prayer-modal Intercessions-modal" role="dialog" aria-label="Intercessions" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="Centcom-prayer-close Intercessions-close" aria-label="Close" onClick={onClose}>&#10005;</button>
        <button
          type="button"
          className={`Intercessions-mode${mode === 'edit' ? ' is-on' : ''}`}
          aria-label={mode === 'edit' ? 'Done editing' : 'Edit'}
          aria-pressed={mode === 'edit'}
          onClick={() => setMode((m) => (m === 'edit' ? 'pray' : 'edit'))}
        >
          {mode === 'edit' ? 'Done' : '✎'}
        </button>
        <h2 className="Intercessions-title">Intercessions</h2>
        <div className="Intercessions-divider" />

        {error && <div className="Intercessions-error" role="alert">{error}</div>}
        {rows === null && !error && <div className="Intercessions-loading">Loading…</div>}

        {rows !== null && mode === 'pray' && (
          <div className="Intercessions-pray" data-testid="pray-mode">
            {prayModel.length === 0 && <div className="Intercessions-empty">Nothing to pray yet. Tap ✎ to add a line.</div>}
            {prayModel.map((entry) => (
              <div key={entry.id} className={`Intercessions-entry Intercessions-entry--${entry.kind.toLowerCase()}`} data-testid={`pray:${entry.id}`}>
                {entry.lines.map((line, i) => (
                  <p key={i} className={`Intercessions-line Intercessions-line--${line.role}`}>
                    {line.role === 'guide' ? `(${line.text})` : line.role === 'pause' ? line.text : (
                      <>
                        <span className="Intercessions-role">{ROLE_LABEL[line.role]}</span> {line.text}
                      </>
                    )}
                  </p>
                ))}
              </div>
            ))}
          </div>
        )}

        {rows !== null && mode === 'edit' && (
          <div className="Intercessions-edit" data-testid="edit-mode">
            {ordered.map((row, i) => (
              <EditRow
                key={row.id}
                row={row}
                first={i === 0}
                last={i === ordered.length - 1}
                onPatch={(p) => patch(row, p)}
                onMove={(dir) => move(row, dir)}
                onRemove={() => remove(row)}
              />
            ))}

            <form className="Intercessions-add" onSubmit={addIntention}>
              <div className="Intercessions-add-label">Add an intention</div>
              <TitleInput value={draft} onChange={setDraft} placeholder="For …" ariaLabel="New intention" />
              <div className="Intercessions-add-row">
                <select className="Intercessions-select" aria-label="Guide" value={band} onChange={(e) => setBand(e.target.value)}>
                  {BANDS.map((b) => <option key={b.id} value={b.id}>{b.guide}</option>)}
                </select>
                <button type="submit" className="Intercessions-btn" disabled={busy || !draft.trim() || titleTooLong(draft)}>Add</button>
              </div>
            </form>

            {!lineOpen && (
              <button type="button" className="Intercessions-link" onClick={() => { setLineOpen(true); setLineAfter(ordered[ordered.length - 1]?.id || ''); }}>
                + Add a line
              </button>
            )}
            {lineOpen && (
              <form className="Intercessions-add Intercessions-add--line" onSubmit={addLine}>
                <div className="Intercessions-add-label">Add a line</div>
                <TitleInput value={lineDraft} onChange={setLineDraft} placeholder="The line as written" ariaLabel="New line" />
                <div className="Intercessions-add-row">
                  <select className="Intercessions-select" aria-label="Kind" value={lineKind} onChange={(e) => setLineKind(e.target.value)}>
                    {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                  <select className="Intercessions-select Intercessions-select--after" aria-label="After" value={lineAfter} onChange={(e) => setLineAfter(e.target.value)}>
                    {ordered.map((r) => <option key={r.id} value={r.id}>after: {String(r.Title || '').slice(0, 32)}</option>)}
                  </select>
                </div>
                <div className="Intercessions-add-row">
                  <button type="button" className="Intercessions-link" onClick={() => setLineOpen(false)}>Cancel</button>
                  <button type="submit" className="Intercessions-btn" disabled={busy || !lineDraft.trim() || titleTooLong(lineDraft)}>Add line</button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>

      {toast && (
        <div className="Intercessions-toast" onClick={(e) => e.stopPropagation()}>
          <UndoToast text={toast.text} ttl={toast.ttl} onUndo={toast.onUndo} />
        </div>
      )}
    </div>
  );
}

// A Title field with the 255 counter. The browser blocks past the cap
// (maxLength) and the counter turns red at 240+ so the wall is visible
// before it is hit.
function TitleInput({ value, onChange, placeholder, ariaLabel, autoFocus, onBlur, onKeyDown }) {
  const n = titleLength(value);
  return (
    <div className="Intercessions-titlefield">
      <textarea
        className="Intercessions-input"
        aria-label={ariaLabel}
        placeholder={placeholder}
        value={value}
        maxLength={TITLE_MAX}
        rows={2}
        autoFocus={autoFocus}
        onChange={(e) => onChange([...e.target.value].slice(0, TITLE_MAX).join(''))}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
      />
      <span className={`Intercessions-counter${n >= TITLE_MAX - 15 ? ' is-near' : ''}`} aria-live="polite">{n}/{TITLE_MAX}</span>
    </div>
  );
}

function EditRow({ row, first, last, onPatch, onMove, onRemove }) {
  const [editing, setEditing] = useState(null);   // 'title' | 'guide' | null
  const [text, setText] = useState('');
  const active = isActive(row);
  const guide = guideOf(row);

  const start = (field) => { setEditing(field); setText(field === 'title' ? String(row.Title || '') : guide); };
  const commit = () => {
    if (editing === 'title') {
      const t = text.trim();
      if (t && t !== String(row.Title || '') && !titleTooLong(t)) onPatch({ title: t });
    } else if (editing === 'guide') {
      const g = text.trim();
      if (g !== guide) onPatch({ guide: g });
    }
    setEditing(null);
  };
  const cancel = () => setEditing(null);
  const keys = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); cancel(); }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commit(); }
  };

  return (
    <div className={`Intercessions-row${active ? '' : ' is-off'}`} data-testid={`edit:${row.id}`}>
      <input
        type="checkbox"
        className="Intercessions-check"
        aria-label={`Active: ${String(row.Title || '').slice(0, 40)}`}
        checked={active}
        onChange={(e) => onPatch({ active: e.target.checked })}
      />
      <div className="Intercessions-row-body">
        {editing === 'title' ? (
          <TitleInput value={text} onChange={setText} ariaLabel="Edit line" autoFocus onBlur={commit} onKeyDown={keys} />
        ) : (
          <button type="button" className="Intercessions-tap Intercessions-tap--title" onClick={() => start('title')}>{row.Title}</button>
        )}
        {editing === 'guide' ? (
          <input
            className="Intercessions-input Intercessions-input--guide"
            aria-label="Edit guide"
            placeholder="Guide (optional)"
            value={text}
            autoFocus
            onChange={(e) => setText(e.target.value)}
            onBlur={commit}
            onKeyDown={keys}
          />
        ) : (
          <button type="button" className={`Intercessions-tap Intercessions-tap--guide${guide ? '' : ' is-empty'}`} onClick={() => start('guide')}>
            {guide ? `(${guide})` : '+ guide'}
          </button>
        )}
        <div className="Intercessions-row-tools">
          <select className="Intercessions-select Intercessions-select--kind" aria-label="Kind" value={KINDS.includes(row.Kind) ? row.Kind : 'Leader'} onChange={(e) => onPatch({ kind: e.target.value })}>
            {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <button type="button" className="Intercessions-icon" aria-label="Move up" disabled={first} onClick={() => onMove(-1)}>↑</button>
          <button type="button" className="Intercessions-icon" aria-label="Move down" disabled={last} onClick={() => onMove(1)}>↓</button>
          <button type="button" className="Intercessions-icon Intercessions-icon--x" aria-label={`Remove: ${String(row.Title || '').slice(0, 40)}`} onClick={onRemove}>✕</button>
        </div>
      </div>
    </div>
  );
}
